import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import {
  Server,
  Zap,
  Shield,
  Clock,
  ArrowRight,
  Star,
  Users,
  Globe,
  CheckCircle,
  PlayCircle,
  Folder,
  MonitorCog,
  ShieldBan,
  ShieldEllipsis,
  ArrowLeft,
  ChevronRight,
  HardDrive,
  Headphones,
  BadgeCheck
} from 'lucide-react';
import './Home.css';


const plans = [
  {
    id: 'mc-dev',
    name: 'Minecraft | DEV',
    type: 'minecraft',
    color: 'green',
    price: '1.99€/mois',
    features: ['2x Ryzen 9 7950X3D', '4 GB DDR5', '32 GB NVMe']
  },
  {
    id: 'nl-premier',
    name: 'Nova-Life | Premier',
    type: 'novalife',
    color: 'green',
    price: '3.99€/mois',
    features: ['2 vCPU', '8 Go RAM', '25 Go stockage']
  },
  {
    id: 'fivem-dev',
    name: 'Fivem | Dev',
    type: 'fivem',
    color: 'green',
    price: '1.99€/mois',
    features: ['1 vCPU', '3 Go RAM', '25 Go stockage']
  },
  {
    id: 'gmod-dev',
    name: 'Garrys Mod | Dev',
    type: 'gmod',
    color: 'green',
    price: '1.99€/mois',
    features: ['2 vCPU', '6 Go RAM', '12 Go stockage']
  },
  {
    id: 'vps-starter',
    name: 'VPS Starter',
    type: 'vps',
    color: 'green',
    price: '4.99€/mois',
    features: ['1 vCPU', '1 Go RAM', '25 Go SSD']
  },
  // ...ajoute les autres offres ici
];

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState('initial');
  type GameConfig = {
    [key: string]: string[];
  };
  
  const [selectedGame, setSelectedGame] = useState<any>(null);
  const [gameConfig, setGameConfig] = useState<GameConfig>({});
  const [recommendedPlan, setRecommendedPlan] = useState<any>(null);
  const [selectedCount, setSelectedCount] = useState<string>('');
  

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const games = [
    {
      id: 'novalife',
      name: 'NovaLife',
      description: 'Serveur roleplay nouvelle génération',
      icon: '🌟',
      configs: {
        players: { label: 'Nombre de joueurs', options: ['10-50', '50-100', '100-200', '200+'] },
        plugins: { label: 'Plugins spécialisés', options: ['Économie avancée', 'Système de métiers', 'Véhicules personnalisées', 'Chat vocal intégré'] },
        features: { label: 'Fonctionnalités', options: ['Interface custom', 'Base de données étendue', 'Synchronisation multi-serveurs'] }
      }
    },
    {
      id: 'fivem',
      name: 'FiveM',
      description: 'Serveur GTA RP personnalisé',
      icon: '🚗',
      configs: {
        players: { label: 'Nombre de joueurs', options: ['32 joueurs', '64 joueurs', '128 joueurs', '200+ joueurs'] },
        plugins: { label: 'Scripts populaires', options: ['ESX Framework', 'QBCore', 'vRP', 'Scripts custom'] },
        features: { label: 'Ressources', options: ['Véhicules HD', 'Maps personnalisées', 'Système bancaire', 'Inventaire avancé'] }
      }
    },
    {
      id: 'minecraft',
      name: 'Minecraft',
      description: 'Serveur Minecraft haute performance',
      icon: '⛏️',
      configs: {
        players: { label: 'Nombre de joueurs', options: ['10-20', '20-50', '50-100', '100+'] },
        plugins: { label: 'Plugins essentiels', options: ['Spigot/Paper', 'WorldEdit', 'Essentials', 'McMMO', 'Dynmap'] },
        features: { label: 'Type de serveur', options: ['Survie', 'Créatif', 'PvP', 'Modpack', 'Mini-jeux'] }
      }
    },
    {
      id: 'gmod',
      name: "Garry's Mod",
      description: 'Serveur GMod personnalisé',
      icon: '🔧',
      configs: {
        players: { label: 'Nombre de joueurs', options: ['16 joueurs', '32 joueurs', '64 joueurs', '100+ joueurs'] },
        plugins: { label: 'Addons populaires', options: ['DarkRP', 'TTT', 'Sandbox', 'StarWarsRP', 'Addons custom'] },
        features: { label: 'Fonctionnalités', options: ['FastDL intégré', 'Base MySQL', 'Anti-cheat', 'Système de grades'] }
      }
    },
    {
      id: 'discord',
      name: 'Bot Discord',
      description: 'Bot Discord personnalisé',
      icon: '🤖',
      configs: {
        servers: { label: 'Nombre de serveurs', options: ['1-5 serveurs', '5-20 serveurs', '20-100 serveurs', '100+ serveurs'] },
        features: { label: 'Fonctionnalités', options: ['Modération auto', 'Musique', 'Économie virtuelle', 'Système de tickets', 'API custom'] },
        usage: { label: 'Utilisation', options: ['Usage léger', 'Usage modéré', 'Usage intensif', 'Usage enterprise'] }
      }
    },
    {
      id: 'vps',
      name: "VPS",
      description: 'Serveur GMod personnalisé',
      icon: '🖥️',
      configs: {
        os: { label: "System d'exploitation", options: ['Debian', 'Ubuntu', 'Windows server'] },
        plugins: { label: 'quel usage', options: ['Gaming', 'Linux', 'NextCloud'] },
        features: { label: 'Fonctionnalités', options: ['Performance', '', 'Anti-cheat', 'Système de grades'] }
      }
    },
  ];

  const calculateRecommendation = (game: typeof games[number]) => {
    // On prend le premier plan qui correspond au type du jeu sélectionné
    return plans.find(plan => plan.type === game.id) || plans[0];
  };

  const handleGameSelect = (game: typeof games[number]) => {
    setSelectedGame(game);
    setCurrentStep('configure');
    setGameConfig({});
    setRecommendedPlan(null);
  };

  const handleConfigChange = (category: string, value: string) => {
    const newConfig: GameConfig = { ...gameConfig };
    if (!newConfig[category]) newConfig[category] = [];
    
    if (newConfig[category].includes(value)) {
      newConfig[category] = newConfig[category].filter(item => item !== value);
    } else {
      if (category === 'players' || category === 'servers') {
        newConfig[category] = [value];
      } else {
        newConfig[category] = [...newConfig[category], value];
      }
    }
    
    setGameConfig(newConfig);
  };

  const handlePlayerCountSelect = (option: string) => {
    setSelectedCount(option);
    handleConfigChange('players', option);
  };

  const handleGetRecommendation = () => {
    const recommendation = calculateRecommendation(selectedGame);
    setRecommendedPlan(recommendation);
    setCurrentStep('recommendation');
  };

  const resetSelector = () => {
    setCurrentStep('initial');
    setSelectedGame(null);
    setGameConfig({});
    setRecommendedPlan(null);
  };

  const features = [
    {
      icon: <Folder className="feature-icon" />,
      title: "Accès complet aux fichiers",
      description: "Gestion des fichiers facile. Avec nous, vous avez un accès complet à tous vos fichiers pour une personnalisation totale."
    },
    {
      icon: <MonitorCog className="feature-icon" />,
      title: "Panneau dédié",
      description: "Facile à utiliser le dashboard dédié ! Un panel conçu pour que vous vous concentrez sur votre jeu, pas sur le management de votre serveur."
    },
    {
      icon: <Clock className="feature-icon" />,
      title: "Fait tourner ton serveur en tout juste 2 minutes",
      description: "Comparé à d'autres fournisseurs, nous nous appuyons exclusivement sur des processeurs optimisés pour les jeux."
    },
    {
      icon: <ShieldEllipsis className="feature-icon" />,
      title: "Protection DDOS",
      description: "Si votre serveur est attaqué, nous vous couvrons avec une protection DDOS GRATUITE."
    }
  ];

  const stats = [
    { number: "20+", label: "Serveur hébergés" },
    { number: "99.9%", label: "Uptime garanti" },
    { number: "3", label: "Experts" },
    { number: "100%", label: "Taux de satisfaction" }
  ];

  const renderInteractiveSection = () => {
    if (currentStep === 'initial') {
      return (
        <section className="cta-section">
          <div className="cta-background"></div>
          <div className="cta-container">
            <h2 className="cta-title">Trouvez l'offre qui vous correspond</h2>
            <p className="cta-description">
              Répondez à quelques questions simples pour découvrir la solution parfaite pour votre projet
            </p>
            <div className="cta-content">
              <div className="cta-benefits">
                <div className="benefit-item">
                  <CheckCircle className="check-icon" />
                  <span>Recommandation personnalisée</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle className="check-icon" />
                  <span>Configuration optimisée</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle className="check-icon" />
                  <span>Support technique inclus</span>
                </div>
              </div>
              <button 
                className="btn-cta"
                onClick={() => setCurrentStep('games')}
              >
                Voir ce qui me correspond
              </button>
            </div>
          </div>
        </section>
      );
    }

    if (currentStep === 'games') {
      return (
        <section className="cta-section">
          <div className="cta-background"></div>
          <div className="cta-container">
            <div className="flex items-center mb-6">
              <button 
                onClick={resetSelector}
                className="action-button action-button-secondary mr-4"
              >
                <ArrowLeft size={20} className="mr-2" />
                Retour
              </button>
              <h2 className="cta-title mb-0">Choisissez votre jeu</h2>
            </div>
            <p className="cta-description mb-8">
              Sélectionnez le type de serveur que vous souhaitez héberger
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {games.map((game) => (
                <button
                  key={game.id}
                  onClick={() => handleGameSelect(game)}
                  className="player-count-button"
                >
                  <div className="player-count">{game.icon}</div>
                  <div className="player-label">{game.name}</div>
                </button>
              ))}
            </div>
          </div>
        </section>
      );
    }

    if (currentStep === 'configure') {
      return (
        <section className="cta-section">
          <div className="cta-background"></div>
          <div className="cta-container">
            <div className="flex items-center mb-6">
              <button 
                onClick={() => setCurrentStep('games')}
                className="action-button action-button-secondary mr-4"
              >
                <ArrowLeft size={20} className="mr-2" />
                Retour
              </button>
              <h2 className="cta-title mb-0">Configuration {selectedGame.name}</h2>
            </div>
            <p className="cta-description mb-8">
              Personnalisez votre serveur selon vos besoins
            </p>

            <div className="max-w-4xl mx-auto space-y-8">
              {Object.entries(selectedGame.configs).map(([key, config]) => {
                const typedConfig = config as { label: string; options: string[] };
                return (
                  <div key={key} className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">{typedConfig.label}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {typedConfig.options.map((option: string) => (
                        <button
                          key={option}
                          onClick={() => handleConfigChange(key, option)}
                          className={`player-count-button ${
                            gameConfig[key]?.includes(option) ? 'selected' : ''
                          }`}
                        >
                          <div className="player-count">{option}</div>
                          <div className="player-label">{
                            key === 'players' || key === 'servers' 
                              ? typedConfig.label 
                              : ''
                          }</div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}

              <div className="text-center">
                <button
                  onClick={handleGetRecommendation}
                  disabled={Object.keys(gameConfig).length === 0}
                  className="btn-cta disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Obtenir ma recommandation
                </button>
              </div>
            </div>
          </div>
        </section>
      );
    }

    if (currentStep === 'recommendation') {
      return (
        <section className="cta-section">
          <div className="cta-background"></div>
          <div className="cta-container">
            <div className="flex items-center mb-6">
              <button 
                onClick={() => setCurrentStep('configure')}
                className="action-button action-button-secondary mr-4"
              >
                <ArrowLeft size={20} className="mr-2" />
                Modifier
              </button>
              <h2 className="cta-title mb-0">Votre offre recommandée</h2>
            </div>
            
            <div className={`plan-card ${recommendedPlan.color} animate-fade-in-up`} style={{ maxWidth: 400, margin: "0 auto" }}>
              <div className="plan-card-header">
                <h3 className="plan-name">{recommendedPlan.name}</h3>
              </div>
              <div className="plan-card-content">
                <ul>
                  {recommendedPlan.features.map((f: string, i: number) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                <div className="plan-pricing">
                  <div className="price">{recommendedPlan.price}</div>
                </div>
                <button className="plan-button animate-button-hover">
                  <span>Commander</span>
                  <div className="button-shine"></div>
                </button>
              </div>
            </div>

            <div className="text-center mt-6">
              <p className="text-gray-400 text-sm">
                Cette recommandation est basée sur vos besoins spécifiques. 
                Vous pouvez toujours changer d'offre plus tard.
              </p>
            </div>
          </div>
        </section>
      );
    }

    return null;
  };

  return (
    <div className="home">
      <Helmet>
        <title>HOSTWARE®  –  Votre serveur, notre priorité.</title>
        <meta name="description" content="HOSTWARE® : offrir des solutions d’hébergement performantes à des prix accessibles gaming pour Minecraft, FiveM, GMod, Bot Discord et NovaLife. Serveurs haute performance à partir de 0.99€/mois." />
        <meta name="keywords" content="hébergement minecraft, serveur fivem, vps gaming, gmod, novalife, serveur jeu, hébergement pas cher" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hostware.fr/" />
        <meta property="og:title" content="HOSTWARE  –  Votre serveur, notre priorité." />
        <meta property="og:description" content="HOSTWARE® : offrir des solutions d’hébergement performantes à des prix accessibles gaming pour Minecraft, FiveM, GMod, Bot Discord et NovaLife. Serveurs haute performance à partir de 0.99€/mois." />
        <meta property="og:image" content="https://hostware.fr/logo512.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="HOSTWARE  –  Votre serveur, notre priorité." />
        <meta property="twitter:description" content="HOSTWARE® : offrir des solutions d’hébergement performantes à des prix accessibles gaming pour Minecraft, FiveM, GMod, Bot Discord et NovaLife. Serveurs haute performance à partir de 0.99€/mois." />
        <meta property="twitter:image" content="https://hostware.fr/logo512.png" />
        <meta property="twitter:image:width" content="512" />
        <meta property="twitter:image:height" content="512" />
        <meta property="twitter:image:alt" content="Logo HostWare" />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section">
        {/* Animated background elements */}
        <div className="background-animations">
          <div className="bg-element bg-element-1"></div>
          <div className="bg-element bg-element-2"></div>
          <div className="bg-element bg-element-3"></div>
        </div>

        <div className="hero-container">
          <div className={`hero-content ${isVisible ? 'visible' : ''}`}>
            <div className="hero-badge">
              <Star className="badge-icon" />
              <span>Hébergeur le moins cher de l'année</span>
            </div>
            
            <h1 className="hero-title">
              Bienvenue chez
              <span className="gradient-text"> HostWare</span>
            </h1>
            
            <p className="hero-description">
              Votre solution d'hébergement <strong>fiable et ultra-rapide</strong> pour vos serveurs. 
              Rejoignez plus de 20 clients qui nous font confiance.
            </p>

            <div className="hero-buttons">
              {/* Bouton vers la page d'inscription */}
              <a href="https://client.hostware.fr/register" className="btn-primary">
                Commencer maintenant
                <ArrowRight className="btn-icon" />
              </a>

              {/* Bouton vers le Discord */}
              <a href="https://discord.gg/tP7M7DAavW" className="btn-secondary" target="_blank" rel="noopener noreferrer">
                <BadgeCheck className="btn-icon" />
                Discord
              </a>
            </div>
          </div>
        </div>

        {/* Floating cards animation */}
        <div className="floating-card floating-card-1">
          <div className="floating-card-content">
            <Server className="floating-icon" />
          </div>
        </div>
        <div className="floating-card floating-card-2">
          <div className="floating-card-content">
            <Globe className="floating-icon" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-number">
                  {stat.number}
                </div>
                <div className="stat-label">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <div className="features-header">
            <h2 className="features-title">
              Qu'est-ce qui fait que nous sommes parfait?
            </h2>
            <p className="features-description">
              Une infrastructure de pointe conçue pour les performances et la fiabilité
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon-wrapper">
                  {feature.icon}
                </div>
                <h3 className="feature-title">
                  {feature.title}
                </h3>
                <p className="feature-description">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive CTA Section */}
      {renderInteractiveSection()}
    </div>
  );
};

export default Home;